import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  cn,
} from "@devhop/ui";
import {
  Link2,
  Link2Off,
  RefreshCw,
  Share2,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";
import type { FacebookPageListItem } from "../api/types";

const TOKEN_STATUS_LABEL: Record<string, string> = {
  valid: "Token ຖືກຕ້ອງ",
  expired: "Token ໝົດອາຍຸ",
  revoked: "Token ຖືກຖອນ",
  expiring_soon: "Token ໃກ້ໝົດອາຍຸ",
};

function tokenBadgeVariant(
  status: string,
): "default" | "secondary" | "destructive" | "outline" {
  if (status === "valid") return "outline";
  if (status === "expiring_soon") return "secondary";
  return "destructive";
}

function formatTokenLabel(page: FacebookPageListItem): string {
  const base = TOKEN_STATUS_LABEL[page.tokenStatus] ?? page.tokenStatus;
  if (page.tokenStatus === "expiring_soon" && page.tokenExpiresInDays != null) {
    return `${base} (${page.tokenExpiresInDays} ມື້)`;
  }
  return base;
}

type FacebookPageCardProps = {
  page: FacebookPageListItem;
  onConnect: () => void;
  onReconnect: () => void;
  onDisconnect: () => void;
  onCheckToken: () => void;
  connecting?: boolean;
  reconnecting?: boolean;
  disconnecting?: boolean;
  checkingToken?: boolean;
};

export function FacebookPageCard({
  page,
  onConnect,
  onReconnect,
  onDisconnect,
  onCheckToken,
  connecting,
  reconnecting,
  disconnecting,
  checkingToken,
}: FacebookPageCardProps) {
  const isDisconnected = page.status === "disconnected";
  const requiresReconnect =
    isDisconnected ||
    page.status === "token_expired" ||
    page.tokenStatus === "expired" ||
    page.tokenStatus === "revoked";
  const tokenLabel = formatTokenLabel(page);
  const tokenVariant = tokenBadgeVariant(page.tokenStatus);

  return (
    <Card className={cn(isDisconnected && "opacity-75")}>
      <CardHeader className="flex flex-row items-start gap-4 space-y-0">
        <div className="size-14 shrink-0 overflow-hidden rounded-lg border bg-muted">
          <AppImage
            src={page.pagePictureUrl}
            alt={page.pageName}
            className="size-full"
            fallback={
              <div className="flex size-full items-center justify-center text-muted-foreground text-xs">
                FB
              </div>
            }
          />
        </div>
        <div className="min-w-0 flex-1">
          <CardTitle className="truncate text-lg">{page.pageName}</CardTitle>
          {page.pageCategory ? (
            <p className="truncate text-muted-foreground text-sm">
              {page.pageCategory}
            </p>
          ) : null}
          <p className="mt-1 font-mono text-muted-foreground text-xs">
            ID: {page.facebookPageId}
          </p>
        </div>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <Badge
          variant={
            isDisconnected || requiresReconnect ? "secondary" : "default"
          }
        >
          {isDisconnected
            ? "ຕັດການເຊື່ອມແລ້ວ"
            : requiresReconnect
              ? "ຕ້ອງເຊື່ອມ Facebook ໃໝ່"
              : "ເປີດໃຊ້ງານ"}
        </Badge>
        <Badge variant={tokenVariant}>
          <span className="inline-flex items-center gap-1">
            {page.tokenStatus === "valid" ? (
              <ShieldCheck className="size-3" />
            ) : (
              <ShieldAlert className="size-3" />
            )}
            {tokenLabel}
          </span>
        </Badge>
        <Badge variant={page.webhookSubscribed ? "default" : "secondary"}>
          {page.webhookSubscribed ? "Webhook ເຊື່ອມແລ້ວ" : "Webhook ຍັງບໍ່ເຊື່ອມ"}
        </Badge>
      </CardContent>
      <CardFooter className="flex flex-col items-stretch gap-2 border-t bg-muted/30 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-muted-foreground text-xs">
          ເຊື່ອມເມື່ອ: {formatDateTimeLocal(page.connectedAt)}
          {page.webhookSubscribedAt
            ? ` · Webhook: ${formatDateTimeLocal(page.webhookSubscribedAt)}`
            : null}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={checkingToken}
            onClick={onCheckToken}
          >
            <RefreshCw
              className={cn("size-4", checkingToken && "animate-spin")}
            />
            ກວດສອບ token
          </Button>
          {!page.webhookSubscribed ? (
            requiresReconnect ? (
              <Button
                type="button"
                size="sm"
                disabled={reconnecting}
                onClick={onReconnect}
              >
                <Share2 className="size-4" />
                ເຊື່ອມ Facebook ໃໝ່
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                disabled={connecting}
                onClick={onConnect}
              >
                <Link2 className="size-4" />
                ເຊື່ອມ webhook
              </Button>
            )
          ) : null}
          {!isDisconnected ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={disconnecting}
              onClick={onDisconnect}
            >
              <Link2Off className="size-4" />
              ຕັດການເຊື່ອມ
            </Button>
          ) : null}
        </div>
      </CardFooter>
    </Card>
  );
}
